import React, { createContext, useState, useEffect, useMemo } from "react";
import { PageState, PageProviderProps } from "./PageProvider.types";

export const PageContext = createContext<PageState | undefined>(undefined);

export function PageProvider({
  children,
  menuOpen: controlledMenuOpen,
  onToggleMenu,
  currentUserRoles = [],
  menuItems = [],
  activeItemId = "",
  activeSubItemId = "",
  organisationName,
  organisationResolved = true,
}: PageProviderProps) {
  const [uncontrolledMenuOpen, setUncontrolledMenuOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchVisible, setSearchVisible] = useState(false);
  const [helpVisible, setHelpVisible] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [expandedSectionId, setExpandedSectionId] = useState(activeItemId);
  const [expandedSubSectionId, setExpandedSubSectionId] =
    useState(activeSubItemId);

  // activeItemId/activeSubItemId often resolve asynchronously (e.g. once the
  // host's sitemap finishes loading, after this provider's first render) -
  // re-seed the expanded section/sub-section whenever they change, not just
  // on the initial (possibly still-empty) render.
  useEffect(() => {
    setExpandedSectionId(activeItemId);
  }, [activeItemId]);

  useEffect(() => {
    setExpandedSubSectionId(activeSubItemId);
  }, [activeSubItemId]);

  const isControlled = controlledMenuOpen !== undefined;
  const menuOpen = isControlled ? controlledMenuOpen! : uncontrolledMenuOpen;

  const toggleMenu = () => {
    if (isControlled) {
      onToggleMenu?.();
    } else {
      setUncontrolledMenuOpen((previous) => !previous);
    }
  };

  const openMobileMenu = () => setMobileMenuOpen(true);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const toggleSection = (id: string) =>
    setExpandedSectionId((previous) => (previous === id ? "" : id));

  const toggleSubSection = (id: string) =>
    setExpandedSubSectionId((previous) => (previous === id ? "" : id));

  const hasRole = (role: string) => currentUserRoles.includes(role);
  const hasAnyRole = (roles: string[]) =>
    roles.some((role) => currentUserRoles.includes(role));

  const value: PageState = useMemo(
    () => ({
      menuOpen,
      toggleMenu,
      mobileMenuOpen,
      openMobileMenu,
      closeMobileMenu,
      searchVisible,
      setSearchVisible,
      helpVisible,
      setHelpVisible,
      settingsVisible,
      setSettingsVisible,
      currentUserRoles,
      hasRole,
      hasAnyRole,
      menuItems,
      activeItemId,
      activeSubItemId,
      expandedSectionId,
      expandedSubSectionId,
      toggleSection,
      toggleSubSection,
      organisationName,
      organisationResolved,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      menuOpen,
      mobileMenuOpen,
      searchVisible,
      helpVisible,
      settingsVisible,
      currentUserRoles,
      menuItems,
      activeItemId,
      activeSubItemId,
      expandedSectionId,
      expandedSubSectionId,
      organisationName,
      organisationResolved,
    ]
  );

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
}
